import type { EMeetingStatus, ITranscriptTextEdit } from "./meetings";

export interface ITranscriptEntry {
  start: number;
  speaker: string;
  text: string;
}

// NOTE: NSAI-6574 — a transcript row as the meeting screens hold it. Its index
// in the stored transcript travels with it, so an edit goes back into the row
// it came from instead of the shown rows replacing the stored ones.
export interface IParsedTranscriptEntry extends ITranscriptEntry {
  sourceIndex: number;
}

export interface IParseTranscriptEntriesProps {
  // NOTE: unknown — `IMeeting.transcriptEntries` is untyped on the wire: the
  // processors' WhisperX segments, and whatever earlier saves wrote.
  transcriptEntries: unknown;
}

export interface IBuildTranscriptTextEditsProps {
  // NOTE: unknown — the stored transcript exactly as the server returned it.
  storedEntries: unknown;
  editedEntries: readonly IParsedTranscriptEntry[];
}

export interface IApplyTranscriptTextEditsProps {
  // NOTE: unknown — the stored transcript exactly as the server returned it.
  transcriptEntries: unknown;
  edits: readonly ITranscriptTextEdit[];
}

export interface ICountSpeakerSegmentsProps {
  entries: readonly ITranscriptEntry[];
  speaker: string;
}

export interface IBuildSpeakerNameSuggestionsProps {
  entries: readonly ITranscriptEntry[];
  // NOTE: unknown — `IMeeting.participants` is untyped on the wire.
  participants: unknown;
  currentName: string;
}

export enum EMediaAvailability {
  AVAILABLE = "available",
  PROCESSING = "processing",
  UNAVAILABLE = "unavailable",
}

export enum EMediaType {
  AUDIO = "audio",
  VIDEO = "video",
  // NOTE: NSAI-3847 — "Download all": the recording, the summary and the
  // transcript packed into a single .zip. Tracked like any other download (one
  // progress ring, one cancel) because the recording is what takes the time.
  BUNDLE = "bundle",
}

export interface IStructuredNote {
  time_start: string;
  time_end: string;
  theme: string;
  emoji?: string;
  points: string[];
}

export interface INotesTimeEntry {
  timeRange: string;
  points: string[];
}

export interface IGroupedNotesByThemeItem {
  theme: string;
  emoji?: string;
  timeEntries: INotesTimeEntry[];
}

export interface ISimpleActionItem {
  time_start: string;
  time_end: string;
  description: string;
}

export interface ILegacyActionItemsSection {
  section: string;
  timeRange: string;
  items: string[];
}

export interface IGetLanguageDisplayNameParams {
  language: string;
  t: (key: string) => string;
}

export interface IParseStructuredNotesParams {
  notes: unknown;
  fallback: string;
}

export interface IFormatSecondsToDurationLabelProps {
  seconds: number;
}

export interface IGetShouldShowShortMeetingMessageProps {
  status: EMeetingStatus;
  secondsConsumed: number;
  notes: unknown;
  transcript: string | null;
}

export interface IGetDurationDisplayProps {
  secondsConsumed: number;
}

// NOTE: Callbacks the "video is still processing" poll loop reports through.
// `checkAvailability` re-asks the server (both players already own a function
// that also refreshes the stream URL as a side effect), and exactly one of
// `onAvailable` / `onUnavailable` fires before the loop stops.
export interface IStartVideoProcessingPollParams {
  checkAvailability: () => Promise<EMediaAvailability>;
  onAvailable: () => void;
  onUnavailable: () => void;
}
